<template>
    <div class="flex flex-column md:p-4">

        <div class="flex flex-column justify-content-center align-items-center w-full h-full">
            <span class="H6-Medium py-4">Reschedule Class</span>

            <form @submit.prevent="submitRequest" class="form w-full">
                <div class="grid justify-content-center align-items-start w-full">

                    <div class="p-3 flex flex-column col-12 md:col-6">

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="student" class="Label align-self-start">Student</label>
                            <InputText :value="displayDetails.studentName" class="w-full" variant="filled" size="small"
                                disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="subject" class="Label align-self-start">Subject</label>
                            <InputText :value="displayDetails.subject" class="w-full" variant="filled" size="small"
                                disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="classModel" class="Label align-self-start">Class Mode</label>
                            <InputText :value="displayDetails.classModel" class="w-full" variant="filled" size="small"
                                disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="language" class="Label align-self-start">Languages</label>
                            <InputText :value="displayDetails.language" class="w-full" variant="filled" size="small"
                                disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="location" class="Label align-self-start">Class Location</label>
                            <InputText :value="displayDetails.location" class="w-full" variant="filled" size="small"
                                placeholder="Class Location" disabled />
                        </div>

                    </div>

                    <div class="p-3 flex flex-column col-12 md:col-6">

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="classType" class="Label align-self-start">Class Type</label>
                            <InputText :value="displayDetails.classType" class="w-full" variant="filled" size="small"
                                disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="address" class="Label align-self-start">Location Address</label>
                            <InputText id="address" :value="form.locationAddress" class="w-full" variant="filled"
                                size="small" placeholder="Location Address" disabled />
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="date" class="Label align-self-start">Date</label>
                            <IconField class="w-full">
                                <DatePicker v-model="form.date" dateFormat="dd/mm/yy" showIcon fluid iconDisplay="input"
                                    class="w-full" variant="filled" size="small" />
                            </IconField>
                            <small v-if="errors.date" class="error-text">{{ errors.date }}</small>
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label class="Label align-self-start">Time Slot</label>
                            <div class="flex flex-column sm:flex-row gap-2 w-full">
                                <DatePicker v-model="form.startTime" timeOnly hourFormat="12" showIcon fluid
                                    iconDisplay="input" variant="filled" class="w-full" placeholder="Start Time"
                                    size="small" :invalid="!!errors.startTime"
                                    @date-select="validateTimeSlot" />
                                <DatePicker v-model="form.endTime" :minDate="form.startTime" timeOnly hourFormat="12" showIcon fluid
                                    iconDisplay="input" variant="filled" class="w-full" placeholder="End Time"
                                    size="small" :invalid="!!errors.endTime"
                                    @date-select="validateTimeSlot" />
                            </div>
                            <small v-if="errors.startTime || errors.endTime" class="error-text">
                                {{ errors.startTime ? 'Start time is required' : errors.endTime }}
                            </small>
                        </div>

                        <div class="flex flex-column gap-2 mb-5 relative">
                            <label for="other_students" class="Label align-self-start">Other Students</label>
                            <InputText id="other_students" :value="form.otherStudents" class="w-full" variant="filled"
                                size="small" disabled />
                        </div>
                    </div>

                    <div class="col-12 p-3 flex flex-column gap-3" v-if="form.hasRepeat">
                        <label class="Label align-self-start">Reschedule Scope</label>
                        <div class="flex flex-column sm:flex-row gap-2 w-full mb-4">
                            <div class="flex align-items-center gap-3 w-full">
                                <RadioButton v-model="form.scope" inputId="scopeSingle" name="scope" value="single" />
                                <label for="scopeSingle" class="Paragraph-Regular">Only this class</label>
                            </div>
                            <div class="flex align-items-center gap-3 w-full">
                                <RadioButton v-model="form.scope" inputId="scopeAll" name="scope" value="all" />
                                <label for="scopeAll" class="Paragraph-Regular">All upcoming classes</label>
                            </div>
                        </div>

                        <div v-if="form.scope === 'all'"
                            class="flex flex-column justify-content-start align-self-start gap-4 mb-5">
                            <div class="flex flex-wrap align-items-center gap-2">
                                <span class="placeholder-text">Repeat every</span>
                                <InputNumber v-model="form.repeatFrequency" inputId="repeatFrequency" variant="filled"
                                    size="small" class="text-center" disabled :pt="{
                                        root: { class: 'p-inputnumber' },
                                        input: { class: 'text-center border-gray-300' }
                                    }" />
                                <span class="placeholder-text pl-2">Week(s)</span>
                            </div>
                            <div class="flex align-items-center gap-4 w-full">
                                <span class="whitespace-nowrap placeholder-text pt-1 ">Repeat on</span>
                                <div class="flex gap-2 flex-wrap overflow-auto p-1">
                                    <Button :label="day.label" variant="text" raised v-for="day in days" :key="day.code"
                                        @click="toggleDay(day.code)" :class="[
                                            ' border-1 teal-button-small duration-150',
                                            form.repeatDays.includes(day.code)
                                                ? 'bg-teal-500 border-teal-500 text-white'
                                                : 'bg-white border-gray-600 text-gray-600 hover:bg-gray-100'
                                        ]" />
                                </div>
                            </div>
                            <div class="flex flex-wrap align-items-center gap-2">
                                <span class="placeholder-text pr-4">Ends after</span>
                                <InputNumber v-model="form.endsAfterOccurrences" inputId="endsAfterOccurrences"
                                    variant="filled" size="small" class="text-center" disabled :pt="{
                                        root: { class: 'p-inputnumber' },
                                        input: { class: 'text-center border-gray-300' }
                                    }" />
                                <span class="placeholder-text pl-2">Occurrence(s)</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 flex flex-column gap-2 mb-4 p-3" v-if="form.areasToImprove">
                        <label for="areasToImprove" class="Label align-self-start">Specific areas want to
                            improve</label>
                        <IconField class="w-full w-full flex align-items-start">
                            <InputIcon class="pi pi-file" />
                            <Textarea v-model="form.areasToImprove" variant="filled" autoResize rows="5" class="w-full"
                                disabled />
                        </IconField>
                    </div>

                    <div class="col-12 flex flex-column gap-2 mb-4 p-3" v-if="form.note">
                        <label for="note" class="Label align-self-start">Note</label>
                        <IconField class="w-full w-full flex align-items-start">
                            <InputIcon class="pi pi-file" />
                            <Textarea v-model="form.note" variant="filled" autoResize rows="5" class="w-full"
                                disabled />
                        </IconField>
                    </div>

                    <div v-if="localConflicts.items?.length > 0" class="col-12 p-3">
                        <div class="conflict-box">
                            <div class="flex align-items-center justify-content-between gap-2 mb-2">
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-exclamation-triangle conflict-icon text-xl"></i>
                                    <span class="Paragraph-Medium conflict-title">
                                        {{ isTutorConflict ? 'Your Schedule Has a Conflict' : 'Student Already Has a Class Scheduled' }}
                                    </span>
                                </div>
                                <Button icon="pi pi-times" text rounded size="small" class="conflict-close p-0" @click="localConflicts = { message: '', items: [] }" />
                            </div>
                            <p class="Label mb-3">
                                {{ isTutorConflict
                                    ? 'You already have a class scheduled during this time. Please choose a different time slot.'
                                    : 'The student already has a class scheduled during this time. Please choose a different time slot.' }}
                            </p>
                            <div v-for="(conflict, i) in localConflicts.items" :key="i" class="conflict-row">
                                <div class="flex flex-column gap-1">
                                    <span class="Label"><span class="font-semibold">Date:</span> {{ conflict.date }}</span>
                                    <span class="Label"><span class="font-semibold">{{ isTutorConflict ? 'Your existing class:' : "Student's existing class:" }}</span>
                                        {{ conflict.existingStart }} – {{ conflict.existingEnd }}
                                    </span>
                                    <span class="Label"><span class="font-semibold">Requested time:</span> {{ conflict.newStart }} – {{ conflict.newEnd }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 flex justify-content-end p-3">
                        <Button label="Send Reschedule Request" class="teal-button" type="submit" />
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref, watch, reactive, defineEmits, computed, onMounted } from "vue";
import Button from "primevue/button";
import { usePlatformLimitsStore } from "@/stores/platformLimitsStore";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import DatePicker from 'primevue/datepicker';
import Textarea from 'primevue/textarea';
import RadioButton from 'primevue/radiobutton';
import InputNumber from 'primevue/inputnumber';
import { notifyError } from "@/utils/notification";
import { cleanupLables, toLocalISOString } from "@/utils/cleanups";
import { generateSignedUrl } from "@/api/storage";
import blankProfile from '@/assets/blank-profile.png';

const CLASS_LOCATIONS = [
    { name: "Tutor’s place", code: "tutors_place" },
    { name: "Student’s place", code: "students_place" },
];

const days = [
    { label: 'M', code: 'monday' },
    { label: 'Tu', code: 'tuesday' },
    { label: 'W', code: 'wednesday' },
    { label: 'Th', code: 'thursday' },
    { label: 'F', code: 'friday' },
    { label: 'Sa', code: 'saturday' },
    { label: 'S', code: 'sunday' },
];

const props = defineProps({
    classDetails: { type: Object, required: true },
    conflicts: { type: Object, required: false, default: () => ({ message: '', items: [] }) },
});
const emit = defineEmits(['submit-request']);

const platformLimitsStore = usePlatformLimitsStore();
onMounted(() => { platformLimitsStore.fetchIfNeeded(); });

const locations = ref(CLASS_LOCATIONS);
const initialLoadingComplete = ref(false);
const profileImageUrl = ref('');
const errors = ref({});
const localConflicts = ref({ message: '', items: [] });
const isTutorConflict = computed(() => localConflicts.value.message?.toLowerCase().includes('tutor'));

watch(() => props.conflicts, (val) => {
    localConflicts.value = val?.items?.length ? val : { message: '', items: [] };
}, { immediate: true });

const displayDetails = reactive({
    studentName: '',
    grade: '',
    subject: '',
    classModel: '',
    language: '',
    classType: '',
    classTypeCode: '',
    location: ''
});

const form = reactive({
    location: null,
    locationAddress: null,
    date: null,
    startTime: null,
    endTime: null,
    otherStudents: null,
    areasToImprove: null,
    note: null,
    hasRepeat: false,
    repeatFrequency: 1,
    repeatDays: [],
    endsAfterOccurrences: 1,
    scope: 'single'
});

const validateTimeSlot = () => {
    if (form.startTime && form.endTime) {
        if (form.endTime.getTime() <= form.startTime.getTime()) {
            errors.value.endTime = "End time must be after start time";
            form.endTime = null;
            return false;
        }
        const durationMin = (form.endTime.getTime() - form.startTime.getTime()) / 60000;
        if (durationMin < platformLimitsStore.minSessionDurationMinutes) {
            errors.value.endTime = `Session duration must be at least ${platformLimitsStore.minSessionDurationMinutes} minutes (got ${Math.round(durationMin)} min)`;
            form.endTime = null;
            return false;
        }
    }
    errors.value.endTime = null;
    return true;
};

const validateForm = () => {
    errors.value = {};

    if (!form.date) errors.value.date = "Date is required";
    if (!form.startTime) errors.value.startTime = "Start time is required";
    if (!form.endTime) {
        errors.value.endTime = "Valid end time is required";
    } else if (form.startTime) {
        const durationMin = (form.endTime.getTime() - form.startTime.getTime()) / 60000;
        if (durationMin < platformLimitsStore.minSessionDurationMinutes) {
            errors.value.endTime = `Session duration must be at least ${platformLimitsStore.minSessionDurationMinutes} minutes (got ${Math.round(durationMin)} min)`;
        }
    }

    return Object.keys(errors.value).length === 0;
};

const toggleDay = (dayCode) => {
    const index = form.repeatDays.indexOf(dayCode);
    if (index > -1) form.repeatDays.splice(index, 1);
    else form.repeatDays.push(dayCode);
};

const combineDateAndTime = (dateSource, timeSource) => {
        if (!dateSource || !timeSource) return null;
        const d = new Date(dateSource);
        const t = new Date(timeSource);
        t.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
        return toLocalISOString(t);
};

const submitRequest = () => {
    if (!validateForm()) {
        notifyError("Error", "Please fill all required fields.");
        return;
    }

    const payload = {
        date: form.date ? toLocalISOString(form.date) : null,
        start: combineDateAndTime(form.date, form.startTime),
        end: combineDateAndTime(form.date, form.endTime),
        type: form.hasRepeat ? form.scope : 'single',
        repeatOn: (form.hasRepeat && form.scope === 'all') ? form.repeatDays : undefined
    };
    emit('submit-request', payload);
};

const populateData = (data) => {
    if (!data) return;

    // Populate display details
    displayDetails.studentName = data.studentName || data.student?.username || 'Student';
    displayDetails.grade = cleanupLables(data.grade);
    displayDetails.subject = cleanupLables(data.subject);
    displayDetails.language = cleanupLables(data.language);

    const modelCode = data.classModel;
    displayDetails.classModel = modelCode === 'individual_class' ? 'Individual Class' :
        modelCode === 'group_class' ? 'Group Class' : cleanupLables(modelCode);

    const typeCode = data.classType;
    displayDetails.classType = cleanupLables(typeCode);
    displayDetails.classTypeCode = typeCode;

    form.locationAddress = data.locationAddress || "";

    const locCode = data.classLocation;
    if (locCode) {
        const loc = locations.value.find(l => l.code === locCode);
        if (loc) {
            form.location = loc;
            displayDetails.location = loc.name;
        } else {
            displayDetails.location = cleanupLables(locCode);
        }
    }

    form.date = data.date ? new Date(data.date) : null;

    const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const d = new Date(timeStr);
        if (!isNaN(d.getTime())) return d;

        const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
            let [, h, m, p] = timeMatch;
            h = parseInt(h);
            m = parseInt(m);
            if (p.toUpperCase() === 'PM' && h < 12) h += 12;
            if (p.toUpperCase() === 'AM' && h === 12) h = 0;
            const dateBase = form.date ? new Date(form.date) : new Date();
            dateBase.setHours(h, m, 0, 0);
            return dateBase;
        }
        const baseDateString = form.date ? form.date.toDateString() : new Date().toDateString();
        return new Date(`${baseDateString} ${timeStr}`);
    };

    form.startTime = parseTime(data.start);
    form.endTime = parseTime(data.end);

    form.otherStudents = data.otherStudents ? data.otherStudents.join(', ') : '';

    form.areasToImprove = data.areasToImprove || "";
    form.note = data.note || "";

    if (data.recurrence?.enabled) {
        form.hasRepeat = true;
        form.repeatFrequency = data.recurrence.repeatWeeks || 1;
        form.repeatDays = data.recurrence.repeatOn || [];
        form.endsAfterOccurrences = data.recurrence.totalOccurrences || 1;
    } else {
        form.hasRepeat = false;
    }

    form.scope = 'single';
};

watch(() => form.date, () => { localConflicts.value = { message: '', items: [] }; });
watch(() => form.startTime, () => { localConflicts.value = { message: '', items: [] }; });
watch(() => form.endTime, () => { localConflicts.value = { message: '', items: [] }; });

watch(() => props.classDetails, async (newDetails) => {
    if (newDetails) {
        populateData(newDetails);

        const photoUrl = newDetails.studentPhotoURL || newDetails.student?.profilePhotoURL;
        if (photoUrl) {
            try {
                profileImageUrl.value = await generateSignedUrl(photoUrl);
            } catch {
                profileImageUrl.value = blankProfile;
            }
        } else {
            profileImageUrl.value = blankProfile;
        }
        initialLoadingComplete.value = true;
    }
}, { immediate: true });

</script>

<style scoped>
.teal-button-small {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 12px !important;
    color: var(--orange-500) !important;
}

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    left: 0;
    padding-left: 0.5rem;
}

.conflict-box {
    background: #fdf8ec;
    border: 1px solid #e9a30c;
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
    margin-top: 0.5rem;
}

.conflict-title {
    color: #e9a30c;
}

.conflict-icon {
    color: #e9a30c;
}

.conflict-close {
    color: #e9a30c !important;
}

.conflict-row {
    border-top: 1px dashed #e9a30c80;
    padding-top: 0.75rem;
    margin-top: 0.75rem;
}

.conflict-row:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
}
</style>